import { useState } from "react";
import { PLAN_LABEL } from "./settings/constants";

const C = {
  cream: "#F5F1EA", white: "#FFFFFF", dark: "#163C2F", mid: "#214D3A",
  sage: "#7FB98E", sagePale: "#EAF2ED", muted: "#9B9690", border: "#D4CFC7",
  brass: "#B08D57",
};

// Welcome screen only ("Option B · Split") — colors sampled from the design
// canvas, intentionally a shade off the shared C.dark/C.cream tokens above.
// Keep these local; step1/step2 keep using C.
const WELCOME = { evergreen: "#1F3A2D", cream: "#F4F1EA" };

const WelcomeHomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11.5L12 4l9 7.5" />
    <path d="M5 10v10h14V10" />
    <path d="M9.5 20v-6h5v6" />
  </svg>
);
const WelcomePersonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const WelcomeCardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);
const WelcomeCheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const INP = {
  width: "100%", padding: "11px 14px", fontSize: 14, border: `1px solid ${C.border}`,
  borderRadius: 8, outline: "none", fontFamily: "'Manrope', sans-serif",
  background: "#fff", color: "#2B2B2B", boxSizing: "border-box",
};
const LBL = { fontSize: 12, fontWeight: 600, color: "#2B2B2B", fontFamily: "'Manrope', sans-serif", marginBottom: 5, display: "block" };
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const SETUP_STEPS = [
  { key: "property", label: "Add your first property",
    desc: "Add the address of a property you manage.", btn: "Add property", WelcomeIcon: WelcomeHomeIcon },
  { key: "tenant",   label: "Add your first tenant",
    desc: "Add the person renting from you.", btn: "Add tenant", WelcomeIcon: WelcomePersonIcon },
  { key: "stripe",   label: "Connect Stripe to collect rent",
    desc: "Link your bank to start collecting rent automatically.", btn: "Connect", WelcomeIcon: WelcomeCardIcon },
];

const btnPrimary = {
  background: C.dark, color: C.cream, border: "none", borderRadius: 999,
  padding: "14px 36px", fontWeight: 700, fontSize: 14, cursor: "pointer",
  fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.08em",
};
const btnOutline = {
  background: "none", color: C.dark, border: `1.5px solid ${C.dark}`, borderRadius: 999,
  padding: "12px 28px", fontWeight: 600, fontSize: 13, cursor: "pointer",
  fontFamily: "'Manrope', sans-serif",
};

export default function Onboarding({ supabase, session, onComplete, onSkip, companyProfile }) {
  const [phase, setPhase] = useState("welcome");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [prop, setProp] = useState({ address: "", city: "", state: "", zip: "", units: 1 });
  const [propertySkipped, setPropertySkipped] = useState(false);
  const [skipTenant, setSkipTenant] = useState(false);
  const [tenant, setTenant] = useState({ firstName: "", lastName: "", email: "", unit: "", leaseStart: "", rent: "" });

  const firstName = session?.user?.user_metadata?.full_name?.trim().split(/\s+/)[0] || null;

  const done = {
    property: false,
    tenant: false,
    stripe: companyProfile?.stripe_connect_status === "connected",
  };

  const propValid = prop.address.trim() && prop.city.trim() && prop.state && prop.zip.trim();
  const tenantValid = propertySkipped || skipTenant || (tenant.firstName.trim() && tenant.rent);

  const handleSave = async () => {
    // Property step was skipped — nothing was filled in, so nothing gets
    // written. A tenant can't be attached to a property that doesn't exist.
    if (propertySkipped) { onComplete(); return; }

    setSaving(true);
    setError(null);
    const uid = session?.user?.id;

    // Resolve landlord server-side via team_members lookup instead of
    // trusting user_metadata.landlord_id, which is client-writable via
    // supabase.auth.updateUser().
    const { data: ownerCtx, error: ownerCtxErr } = await supabase.functions.invoke("get-owner-context");
    if (ownerCtxErr) {
      setError("Could not verify your account: " + ownerCtxErr.message);
      setSaving(false);
      return;
    }
    const landlordId = ownerCtx?.owner_id ?? uid;

    const { error: propErr } = await supabase.from("properties").insert([{
      address: prop.address.trim(),
      city: prop.city.trim(),
      state: prop.state,
      zip: prop.zip.trim(),
      units: parseInt(prop.units) || 1,
      user_id: landlordId,
    }]);
    if (propErr) { setError("Could not save property: " + propErr.message); setSaving(false); return; }

    if (!skipTenant && tenant.firstName.trim()) {
      const name = [tenant.firstName.trim(), tenant.lastName.trim()].filter(Boolean).join(" ");
      const tenantPayload = {
        name,
        email: tenant.email.trim() || null,
        property: tenant.unit.trim() || null,
        address: prop.address.trim(),
        city: prop.city.trim(),
        state: prop.state,
        zip: prop.zip.trim(),
        lease_start: tenant.leaseStart || null,
        rent: tenant.rent ? Math.round(parseFloat(tenant.rent)) : null,
        due_day: 1,
        status: "active",
        user_id: landlordId,
      };
      const { error: tenErr } = await supabase.from("tenants").insert([tenantPayload]);
      if (tenErr) { setError("Property saved, but could not save tenant: " + tenErr.message); setSaving(false); onComplete(); return; }

      if (tenantPayload.email) {
        try {
          await supabase.functions.invoke("send-tenant-welcome", {
            body: { tenant: tenantPayload, landlordId, origin: window.location.origin },
          });
        } catch (e) {
          console.warn("[Onboarding] welcome email error:", e);
        }
      }
    }

    setSaving(false);
    onComplete();
  };

  // ── Welcome screen ("Option B · Split") ──
  if (phase === "welcome") {
    const tier = session?.user?.user_metadata?.tier || "free";
    const planLine = tier === "free"
      ? "Free plan · unlimited properties, rent collection included"
      : `${PLAN_LABEL[tier] || "Free"} plan`;
    const completedCount = Object.values(done).filter(Boolean).length;
    const stepAction = { property: () => setPhase("step1"), tenant: () => setPhase("step1"), stripe: onComplete };

    return (
      <div style={{ fontFamily: "'Manrope', sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&display=swap" rel="stylesheet" />
        <style>{`
          .ob-split { display: flex; min-height: 100vh; }
          .ob-left { flex: 0 0 42%; background: ${WELCOME.evergreen}; color: ${WELCOME.cream}; padding: 56px 48px; display: flex; flex-direction: column; justify-content: space-between; }
          .ob-right { flex: 1; background: #FFFFFF; display: flex; align-items: center; justify-content: center; padding: 56px 48px; }
          .ob-right-inner { max-width: 440px; width: 100%; }
          @media (max-width: 860px) {
            .ob-split { flex-direction: column; }
            .ob-left, .ob-right { flex: none; width: 100%; padding: 40px 28px; }
          }
        `}</style>
        <div className="ob-split">
          <div className="ob-left">
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 40 }}>
                <img src="/leasly-logo.png" alt="Leasly" width="32" height="32" style={{ borderRadius: 7 }} />
                <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 700, color: WELCOME.cream }}>Leasly</span>
              </div>
              <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400, fontSize: 46, lineHeight: 1.15, color: WELCOME.cream, margin: "0 0 20px" }}>
                {firstName ? `Welcome to Leasly, ${firstName}!` : "Welcome to Leasly!"}
                <br />Let's set up your account.
              </h1>
              <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: 15, lineHeight: 1.7, color: "rgba(244,241,234,0.8)", maxWidth: 400 }}>
                Your properties, tenants, rent and records in one place. Complete these three steps whenever you're ready.
              </p>
            </div>
            <div>
              <div style={{ borderTop: "1px solid rgba(244,241,234,0.2)", marginBottom: 16 }} />
              <div style={{ fontSize: 13, color: "rgba(244,241,234,0.65)", lineHeight: 1.6 }}>{planLine}</div>
            </div>
          </div>

          <div className="ob-right">
            <div className="ob-right-inner">
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
                <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 24, color: C.dark, margin: 0 }}>Account setup</h2>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{completedCount} of 3 complete</span>
              </div>
              <div style={{ height: 4, background: C.border, borderRadius: 2, marginBottom: 32, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${(completedCount / 3) * 100}%`, background: C.sage, borderRadius: 2, transition: "width 0.2s" }} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 32 }}>
                {SETUP_STEPS.map(s => (
                  <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                      background: done[s.key] ? C.sagePale : "#fff", border: `1.5px solid ${done[s.key] ? C.sage : C.border}`,
                      color: done[s.key] ? C.sage : C.dark,
                    }}>
                      {done[s.key] ? <WelcomeCheckIcon /> : <s.WelcomeIcon />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.dark }}>{s.label}</div>
                      <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>{s.desc}</div>
                    </div>
                    <button onClick={stepAction[s.key]} style={{ ...btnOutline, flexShrink: 0, padding: "9px 18px", fontSize: 12.5 }}>
                      {s.btn}
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <button onClick={onSkip} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "'Manrope', sans-serif", textDecoration: "underline", padding: 0 }}>
                  Skip for now
                </button>
                <button onClick={onComplete} style={{ ...btnPrimary, padding: "12px 28px" }}>Go to my dashboard</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Step indicator ──
  const stepNum = phase === "step1" ? 1 : 2;
  const StepIndicator = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 28 }}>
      {[1, 2].map(n => (
        <div key={n} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
            background: n <= stepNum ? C.dark : C.cream,
            color: n <= stepNum ? C.cream : C.muted,
            border: `1.5px solid ${n <= stepNum ? C.dark : C.border}`,
          }}>{n}</div>
          {n < 2 && <div style={{ width: 40, height: 2, background: stepNum > 1 ? C.dark : C.border, borderRadius: 1 }} />}
        </div>
      ))}
    </div>
  );

  // ── Step 1: Property ──
  if (phase === "step1") {
    return (
      <div style={{ minHeight: "100vh", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Manrope', sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 480, width: "100%" }}>
          <StepIndicator />
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px 28px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>🏠</span>
              <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 24, color: C.dark, margin: 0 }}>Add your first property</h2>
            </div>
            <p style={{ fontSize: 13, color: C.muted, marginBottom: 24, lineHeight: 1.6 }}>Enter the address of your first rental property.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={LBL}>Street Address *</label>
                <input type="text" placeholder="e.g. 155 West Siino Ave" value={prop.address} onChange={e => setProp(s => ({ ...s, address: e.target.value }))} style={INP} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 2 }}>
                  <label style={LBL}>City *</label>
                  <input type="text" placeholder="e.g. Pittsburg" value={prop.city} onChange={e => setProp(s => ({ ...s, city: e.target.value }))} style={INP} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={LBL}>State *</label>
                  <select value={prop.state} onChange={e => setProp(s => ({ ...s, state: e.target.value }))} style={INP}>
                    <option value="">Select</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={LBL}>Zip *</label>
                  <input type="text" placeholder="94565" value={prop.zip} onChange={e => setProp(s => ({ ...s, zip: e.target.value }))} style={INP} />
                </div>
              </div>
              <div>
                <label style={LBL}>Number of Units</label>
                <input type="number" min="1" max="100" value={prop.units} onChange={e => setProp(s => ({ ...s, units: e.target.value }))} style={{ ...INP, maxWidth: 120 }} />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 28 }}>
              <button onClick={() => setPhase("welcome")} style={btnOutline}>← Back</button>
              <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
                <button onClick={() => { setPropertySkipped(true); setPhase("step2"); }}
                  style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "'Manrope', sans-serif", textDecoration: "underline", padding: 0 }}>
                  Skip for now
                </button>
                <button onClick={() => { if (propValid) { setPropertySkipped(false); setPhase("step2"); } }} disabled={!propValid}
                  style={{ ...btnPrimary, opacity: propValid ? 1 : 0.4, padding: "12px 32px" }}>
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: Tenant (optional) ──
  if (phase === "step2") {
    return (
      <div style={{ minHeight: "100vh", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Manrope', sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 480, width: "100%" }}>
          <StepIndicator />
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px 28px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>👤</span>
              <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 24, color: C.dark, margin: 0 }}>Add your first tenant</h2>
            </div>
            <p style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
              {propertySkipped
                ? "You skipped adding a property, so there's nothing to attach a tenant to yet. You can add both from your dashboard anytime."
                : <>Add a tenant to <strong>{prop.address}</strong>, or skip this step.</>}
            </p>

            {/* Skip toggle */}
            {!propertySkipped && (
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 20, padding: "12px 14px", background: skipTenant ? C.sagePale : C.cream, border: `1.5px solid ${skipTenant ? C.sage : C.border}`, borderRadius: 10 }}>
                <input type="checkbox" checked={skipTenant} onChange={e => setSkipTenant(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: C.dark, cursor: "pointer", flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: C.dark }}>No tenant yet — I'll add one later</span>
              </label>
            )}

            {!propertySkipped && !skipTenant && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={LBL}>First Name *</label>
                    <input type="text" placeholder="First" value={tenant.firstName} onChange={e => setTenant(s => ({ ...s, firstName: e.target.value }))} style={INP} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={LBL}>Last Name</label>
                    <input type="text" placeholder="Last" value={tenant.lastName} onChange={e => setTenant(s => ({ ...s, lastName: e.target.value }))} style={INP} />
                  </div>
                </div>
                <div>
                  <label style={LBL}>Email</label>
                  <input type="email" placeholder="tenant@email.com" value={tenant.email} onChange={e => setTenant(s => ({ ...s, email: e.target.value }))} style={INP} />
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={LBL}>Unit Number</label>
                    <input type="text" placeholder="e.g. Unit 1" value={tenant.unit} onChange={e => setTenant(s => ({ ...s, unit: e.target.value }))} style={INP} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={LBL}>Lease Start</label>
                    <input type="date" value={tenant.leaseStart} onChange={e => setTenant(s => ({ ...s, leaseStart: e.target.value }))} style={INP} />
                  </div>
                </div>
                <div>
                  <label style={LBL}>Monthly Rent *</label>
                  <input type="number" min="0" placeholder="e.g. 1500" value={tenant.rent} onChange={e => setTenant(s => ({ ...s, rent: e.target.value }))} style={{ ...INP, maxWidth: 180 }} />
                </div>
              </div>
            )}

            {error && (
              <div style={{ marginTop: 16, fontSize: 13, color: "#dc2626", background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 28 }}>
              <button onClick={() => setPhase("step1")} style={btnOutline}>← Back</button>
              <button onClick={handleSave} disabled={saving || !tenantValid}
                style={{ ...btnPrimary, opacity: saving || !tenantValid ? 0.4 : 1, padding: "12px 32px" }}>
                {saving ? "Saving…" : propertySkipped ? "Continue" : skipTenant ? "Save Property" : "Save & Continue"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export function OnboardingBanner({ supabase, session, properties, tenants, companyProfile, setShowAddProperty, setShowAddTenant, setPage }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const hasProperty = properties.length > 0;
  const hasTenant = tenants.length > 0;
  const hasStripe = companyProfile?.stripe_connect_status === "connected";
  const allDone = hasProperty && hasTenant && hasStripe;

  if (allDone) {
    if (!companyProfile?.onboarding_complete) {
      supabase.from("company_profiles").update({ onboarding_complete: true })
        .eq("user_id", session?.user?.id).then(() => {});
    }
    return null;
  }

  const nextStep = !hasProperty
    ? { label: "Add your first property", action: () => { setPage("Properties"); setTimeout(() => setShowAddProperty(true), 100); } }
    : !hasTenant
      ? { label: "Add your first tenant", action: () => { setPage("Tenants"); setTimeout(() => setShowAddTenant(true), 100); } }
      : { label: "Connect Stripe to collect rent", action: () => {
          setPage("Settings");
          setTimeout(() => {
            const el = document.getElementById("stripe-connect");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 200);
        }};

  return (
    <div style={{
      background: "#FFFBF0", border: "1px solid #B08D5744", borderRadius: 12,
      padding: "14px 20px", marginBottom: 20, display: "flex",
      alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap",
      fontFamily: "'Manrope', sans-serif",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 200 }}>
        <span style={{ fontSize: 18 }}>✦</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#2B2B2B" }}>Finish setting up your account</div>
          <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2 }}>{nextStep.label}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button onClick={nextStep.action} style={{
          background: "#163C2F", color: "#F5F1EA", border: "none", borderRadius: 999,
          padding: "8px 20px", fontSize: 12, fontWeight: 700, cursor: "pointer",
          fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.06em",
        }}>
          Continue Setup
        </button>
        <button onClick={() => setDismissed(true)} style={{
          background: "none", border: "none", color: "#9B9690", cursor: "pointer",
          fontSize: 16, padding: "4px 8px", lineHeight: 1,
        }}>✕</button>
      </div>
    </div>
  );
}
